import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Brain,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  AlertTriangle,
  Target,
  BarChart3,
  Loader2,
  RefreshCw,
  Plus,
  Trash2,
  ExternalLink,
  Scan,
  CheckCircle,
  XCircle,
  Link
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AIFeatures = () => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [slowMovers, setSlowMovers] = useState([]);
  const [highMargin, setHighMargin] = useState([]);
  const [newProductPricing, setNewProductPricing] = useState({});
  const [competitorPrices, setCompetitorPrices] = useState([]);
  const [priceComparison, setPriceComparison] = useState([]);
  const [categoryPerformance, setCategoryPerformance] = useState([]);
  
  // VKB Scanner state
  const [vkbProducts, setVkbProducts] = useState([]);
  const [vkbMatches, setVkbMatches] = useState([]);
  const [unmatchedVkb, setUnmatchedVkb] = useState([]);
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [selectedVkbProduct, setSelectedVkbProduct] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState("VKB");  // VKB or BUILDIT
  
  // PDF Scanner state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfCompetitorName, setPdfCompetitorName] = useState("NTK");
  const [pdfScanning, setPdfScanning] = useState(false);
  const [pdfProducts, setPdfProducts] = useState([]);
  const [pdfUnmatched, setPdfUnmatched] = useState([]);
  const [pdfMatchSuggestions, setPdfMatchSuggestions] = useState([]);
  const [selectedPdfProduct, setSelectedPdfProduct] = useState(null);
  const [pdfScanResult, setPdfScanResult] = useState(null);
  const [manualSearch, setManualSearch] = useState("");
  const [manualResults, setManualResults] = useState([]);
  const [manualSearching, setManualSearching] = useState(false);

  // Price Health Check state
  const [priceCheckData, setPriceCheckData] = useState(null);
  const [priceCheckLoading, setPriceCheckLoading] = useState(false);
  const [priceCheckFilter, setPriceCheckFilter] = useState("ALL");
  
  // Agrinet sync state
  const [agrinetScraping, setAgrinetScraping] = useState(false);
  const [agrinetProducts, setAgrinetProducts] = useState([]);
  const [agrinetMatches, setAgrinetMatches] = useState([]);
  const [agrinetUpdates, setAgrinetUpdates] = useState([]);
  const [updatingCosts, setUpdatingCosts] = useState(false);
  
  // Competitor price form
  const [showAddCompetitor, setShowAddCompetitor] = useState(false);
  const [competitorForm, setCompetitorForm] = useState({
    product_id: "",
    competitor_name: "VKB LANDBOU",
    competitor_price: "",
    source_url: "",
    notes: ""
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/dashboard/summary`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load AI dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchSlowMovers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/pricing/slow-movers`);
      setSlowMovers(response.data.products || []);
      toast.success(`Found ${response.data.total_slow_movers} slow-moving products`);
    } catch (error) {
      console.error("Error fetching slow movers:", error);
      toast.error("Failed to analyze slow-moving products");
    } finally {
      setLoading(false);
    }
  };

  const fetchHighMargin = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/pricing/high-margin-opportunities`);
      setHighMargin(response.data.products || []);
      toast.success(`Found ${response.data.total_opportunities} margin opportunities`);
    } catch (error) {
      console.error("Error fetching high margin opportunities:", error);
      toast.error("Failed to analyze margin opportunities");
    } finally {
      setLoading(false);
    }
  };

  const fetchNewProductPricing = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/pricing/new-product-suggestions`);
      setNewProductPricing(response.data.categories || {});
    } catch (error) {
      console.error("Error fetching new product pricing:", error);
      toast.error("Failed to load pricing suggestions");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitorPrices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/competitor-prices`);
      setCompetitorPrices(response.data.prices || []);
    } catch (error) {
      console.error("Error fetching competitor prices:", error);
      toast.error("Failed to load competitor prices");
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceComparison = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/competitor-prices/comparison`);
      setPriceComparison(response.data.comparisons || []);
    } catch (error) {
      console.error("Error fetching price comparison:", error);
      toast.error("Failed to load price comparison");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryPerformance = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/insights/category-performance`);
      setCategoryPerformance(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching category performance:", error);
      toast.error("Failed to load category performance");
    } finally {
      setLoading(false);
    }
  };

  const addCompetitorPrice = async () => {
    if (!competitorForm.product_id || !competitorForm.competitor_price) {
      toast.error("Please fill in product ID and competitor price");
      return;
    }

    try {
      await axios.post(`${API}/ai-features/competitor-prices`, {
        ...competitorForm,
        competitor_price: parseFloat(competitorForm.competitor_price),
        last_updated: new Date().toISOString()
      });
      toast.success("Competitor price added");
      setShowAddCompetitor(false);
      setCompetitorForm({
        product_id: "",
        competitor_name: "VKB LANDBOU",
        competitor_price: "",
        source_url: "",
        notes: ""
      });
      fetchCompetitorPrices();
      fetchPriceComparison();
    } catch (error) {
      console.error("Error adding competitor price:", error);
      toast.error("Failed to add competitor price");
    }
  };

  const deleteCompetitorPrice = async (priceId) => {
    try {
      await axios.delete(`${API}/ai-features/competitor-prices/${priceId}`);
      toast.success("Competitor price deleted");
      fetchCompetitorPrices();
      fetchPriceComparison();
    } catch (error) {
      console.error("Error deleting competitor price:", error);
      toast.error("Failed to delete competitor price");
    }
  };

  // VKB Scanner Functions
  const scanVkbPrices = async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API}/ai-features/vkb-scanner/scan`, {
        params: { competitor: selectedCompetitor }
      });
      toast.success(`Found ${response.data.products_found} ${response.data.competitor} products`);
      fetchUnmatchedVkbProducts();
      fetchVkbMatches();
    } catch (error) {
      console.error("Error scanning competitor:", error);
      toast.error("Failed to scan competitor prices");
    } finally {
      setScanning(false);
    }
  };

  const fetchUnmatchedVkbProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/vkb-scanner/unmatchedproducts`, {
        params: { competitor: selectedCompetitor }
      });
      setUnmatchedVkb(response.data.unmatched_products || []);
    } catch (error) {
      console.error("Error fetching unmatched products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVkbMatches = async () => {
    try {
      const response = await axios.get(`${API}/ai-features/vkb-scanner/matches`);
      setVkbMatches(response.data.matches || []);
    } catch (error) {
      console.error("Error fetching matches:", error);
    }
  };

  const fetchMatchSuggestions = async (vkbProductName) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-features/vkb-scanner/suggestions`, {
        params: { 
          vkb_product_name: vkbProductName,
          competitor: selectedCompetitor
        }
      });
      setMatchSuggestions(response.data.suggestions || []);
      setSelectedVkbProduct(response.data.vkb_product);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      toast.error("Failed to load suggestions");
    } finally {
      setLoading(false);
    }
  };

  const matchVkbProduct = async (vkbProductName, yourProductId) => {
    try {
      await axios.post(`${API}/ai-features/vkb-scanner/match`, null, {
        params: {
          vkb_product_name: vkbProductName,
          your_product_id: yourProductId,
          competitor: selectedCompetitor
        }
      });
      toast.success("Products matched successfully!");
      setMatchSuggestions([]);
      setSelectedVkbProduct(null);
      fetchUnmatchedVkbProducts();
      fetchVkbMatches();
      fetchPriceComparison();
    } catch (error) {
      console.error("Error matching products:", error);
      toast.error("Failed to match products");
    }
  };

  const deleteVkbMatch = async (vkbProductName) => {
    try {
      await axios.delete(`${API}/ai-features/vkb-scanner/match/${encodeURIComponent(vkbProductName)}`);
      toast.success("Match removed");
      fetchVkbMatches();
      fetchUnmatchedVkbProducts();
    } catch (error) {
      console.error("Error deleting match:", error);
      toast.error("Failed to remove match");
    }
  };

  // PDF Scanner Functions
  const handlePdfUpload = async () => {
    if (!pdfFile) { toast.error("Please select a PDF file"); return; }
    if (!pdfCompetitorName.trim()) { toast.error("Please enter competitor name"); return; }
    if (pdfFile.size > 20 * 1024 * 1024) { toast.error("PDF too large (max 20MB)"); return; }

    setPdfScanning(true);
    setPdfScanResult(null);
    try {
      const formData = new FormData();
      formData.append("file", pdfFile);
      formData.append("competitor_name", pdfCompetitorName.trim());

      const res = await axios.post(`${API}/ai-features/competitor-pdf/scan`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      setPdfScanResult(res.data);
      setPdfProducts(res.data.products || []);
      toast.success(res.data.message);
      fetchPdfUnmatched(res.data.competitor_key);
    } catch (error) {
      console.error("PDF scan error:", error);
      const msg = error.response?.data?.detail || error.message || "PDF scan failed";
      toast.error(msg);
    } finally {
      setPdfScanning(false);
    }
  };

  const fetchPdfUnmatched = async (key) => {
    const competitorKey = key || pdfCompetitorName.trim().toUpperCase().replace(/ /g, "_");
    try {
      const res = await axios.get(`${API}/ai-features/competitor-pdf/unmatched/${competitorKey}`);
      setPdfUnmatched(res.data.unmatched_products || []);
    } catch { /* ignore */ }
  };

  const fetchPdfSuggestions = async (productName) => {
    const competitorKey = pdfCompetitorName.trim().toUpperCase().replace(/ /g, "_");
    try {
      const res = await axios.get(`${API}/ai-features/competitor-pdf/suggestions`, {
        params: { competitor_product_name: productName, competitor_key: competitorKey }
      });
      setPdfMatchSuggestions(res.data.suggestions || []);
      setSelectedPdfProduct(res.data.competitor_product);
    } catch {
      toast.error("Failed to load suggestions");
    }
  };

  const matchPdfProduct = async (competitorProductName, yourProductId) => {
    const competitorKey = pdfCompetitorName.trim().toUpperCase().replace(/ /g, "_");
    try {
      await axios.post(`${API}/ai-features/competitor-pdf/match`, null, {
        params: { competitor_product_name: competitorProductName, your_product_id: yourProductId, competitor_key: competitorKey }
      });
      toast.success("Products matched!");
      setPdfMatchSuggestions([]);
      setSelectedPdfProduct(null);
      fetchPdfUnmatched(competitorKey);
      fetchPriceComparison();
    } catch {
      toast.error("Failed to match products");
    }
  };

  const handleManualSearch = async (query) => {
    setManualSearch(query);
    if (query.length < 2) { setManualResults([]); return; }
    setManualSearching(true);
    try {
      const res = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}&limit=10`);
      const items = res.data.products || res.data || [];
      setManualResults(items);
    } catch {
      setManualResults([]);
    } finally {
      setManualSearching(false);
    }
  };

  const runPriceCheck = async () => {
    setPriceCheckLoading(true);
    try {
      const res = await axios.get(`${API}/products/price-health-check`);
      setPriceCheckData(res.data);
    } catch {
      toast.error("Failed to run price check");
    } finally {
      setPriceCheckLoading(false);
    }
  };


  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Brain className="h-8 w-8 text-purple-600" />
          <div>
            <h1 className="text-3xl font-bold">AI Features</h1>
            <p className="text-gray-500">Intelligent pricing & business insights</p>
          </div>
        </div>
        <Button onClick={fetchDashboardData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Dashboard Summary Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Products Tracked</p>
                  <p className="text-2xl font-bold">{dashboardData.active_products}</p>
                </div>
                <Package className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Needs Attention</p>
                  <p className="text-2xl font-bold text-orange-600">{dashboardData.products_needing_attention}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Competitor Prices</p>
                  <p className="text-2xl font-bold">{dashboardData.competitor_prices_tracked}</p>
                </div>
                <Target className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Sales (30 days)</p>
                  <p className="text-2xl font-bold">{dashboardData.recent_sales_count}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="vkb-scanner" className="space-y-4">
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="agrinet-sync">Agrinet Sync</TabsTrigger>
          <TabsTrigger value="vkb-scanner">VKB Scanner</TabsTrigger>
          <TabsTrigger value="pdf-scanner">PDF Scanner</TabsTrigger>
          <TabsTrigger value="price-check">Price Check</TabsTrigger>
          <TabsTrigger value="slow-movers">Slow Movers</TabsTrigger>
          <TabsTrigger value="margin-opportunities">High Margin</TabsTrigger>
          <TabsTrigger value="new-products">New Products</TabsTrigger>
          <TabsTrigger value="competitor-prices">Competitors</TabsTrigger>
          <TabsTrigger value="category-performance">Categories</TabsTrigger>
        </TabsList>


        {/* VKB Scanner Tab */}
        <TabsContent value="vkb-scanner" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scan className="h-5 w-5 text-purple-500" />
                Competitor Price Scanner (Auto-Track Prices)
              </CardTitle>
              <CardDescription>
                Automatically scan competitor promotions, match products to your inventory, and track price changes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Label className="mb-2">Select Competitor</Label>
                <Select value={selectedCompetitor} onValueChange={setSelectedCompetitor}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select competitor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VKB">VKB LANDBOU (NTK Limpopo)</SelectItem>
                    <SelectItem value="BUILDIT">Buildit.co.za</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 mb-6">
                <Button onClick={scanVkbPrices} disabled={scanning} className="bg-purple-600 hover:bg-purple-700">
                  {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Scan className="h-4 w-4 mr-2" />}
                  Scan {selectedCompetitor === "BUILDIT" ? "Buildit" : "VKB"} Promotions
                </Button>
                <Button onClick={fetchUnmatchedVkbProducts} variant="outline" disabled={loading}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <Button variant="outline" onClick={() => window.open(selectedCompetitor === "BUILDIT" ? "https://www.buildit.co.za" : "https://www.vkb.co.za", "_blank")}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Visit {selectedCompetitor === "BUILDIT" ? "Buildit" : "VKB"} Website
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="bg-blue-50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-600">VKB Products Found</p>
                    <p className="text-2xl font-bold">{unmatchedVkb.length + vkbMatches.length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-600">Matched & Tracking</p>
                    <p className="text-2xl font-bold text-green-600">{vkbMatches.length}</p>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-600">Needs Matching</p>
                    <p className="text-2xl font-bold text-orange-600">{unmatchedVkb.length}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Matched Products */}
              {vkbMatches.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Matched & Auto-Tracking ({vkbMatches.length})
                  </h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 font-semibold text-sm border-b pb-2">
                      <div className="col-span-3">VKB Product</div>
                      <div className="col-span-3">Your Product</div>
                      <div className="col-span-2 text-right">VKB Price</div>
                      <div className="col-span-2 text-right">Your Price</div>
                      <div className="col-span-1 text-right">Diff</div>
                      <div className="col-span-1"></div>
                    </div>

                    {vkbMatches.slice(0, 10).map((match, index) => {
                      const priceDiff = (match.your_price || 0) - (match.vkb_price || 0);
                      const diffPct = match.vkb_price ? ((priceDiff / match.vkb_price) * 100) : 0;
                      
                      return (
                        <div key={index} className="grid grid-cols-12 gap-2 text-sm py-2 border-b hover:bg-gray-50">
                          <div className="col-span-3 text-xs">{match.vkb_product_name}</div>
                          <div className="col-span-3">
                            <p className="font-medium">{match.your_product_name}</p>
                            <p className="text-xs text-gray-500">PLU: {match.your_plu}</p>
                          </div>
                          <div className="col-span-2 text-right">R{(match.vkb_price || 0).toFixed(2)}</div>
                          <div className="col-span-2 text-right">R{(match.your_price || 0).toFixed(2)}</div>
                          <div className="col-span-1 text-right">
                            <Badge variant={priceDiff > 0 ? "destructive" : "default"} className="text-xs">
                              {diffPct > 0 ? "+" : ""}{diffPct.toFixed(0)}%
                            </Badge>
                          </div>
                          <div className="col-span-1 text-right">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => deleteVkbMatch(match.vkb_product_name)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Unmatched VKB Products */}
              {unmatchedVkb.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Target className="h-5 w-5 text-orange-500" />
                    VKB Products Needing Match ({unmatchedVkb.length})
                  </h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 font-semibold text-sm border-b pb-2">
                      <div className="col-span-5">VKB Product Name</div>
                      <div className="col-span-2">Category</div>
                      <div className="col-span-2 text-right">VKB Price</div>
                      <div className="col-span-3 text-center">Action</div>
                    </div>

                    {unmatchedVkb.slice(0, 20).map((product, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 text-sm py-2 border-b hover:bg-gray-50">
                        <div className="col-span-5">{product.name}</div>
                        <div className="col-span-2 text-xs">{product.category}</div>
                        <div className="col-span-2 text-right font-semibold">R{product.price.toFixed(2)}</div>
                        <div className="col-span-3 text-center">
                          <Button 
                            size="sm" 
                            onClick={() => fetchMatchSuggestions(product.name)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Link className="h-3 w-3 mr-1" />
                            Find Match
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {unmatchedVkb.length === 0 && vkbMatches.length === 0 && !loading && (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Scan className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-2">No VKB products scanned yet</p>
                  <p className="text-sm text-gray-500 mb-4">Click "Scan VKB Promotions Now" to start tracking competitor prices</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Match Suggestions Dialog */}
          {selectedVkbProduct && (
            <Card className="border-2 border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Match Suggestions for: {selectedVkbProduct.name}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      setSelectedVkbProduct(null);
                      setMatchSuggestions([]);
                    }}
                  >
                    <XCircle className="h-5 w-5" />
                  </Button>
                </CardTitle>
                <CardDescription>
                  VKB Price: R{selectedVkbProduct.price.toFixed(2)} | Category: {selectedVkbProduct.category}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {matchSuggestions.length > 0 ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 font-semibold text-sm border-b pb-2">
                      <div className="col-span-4">Your Product</div>
                      <div className="col-span-2 text-right">Your Price</div>
                      <div className="col-span-2 text-right">Difference</div>
                      <div className="col-span-2 text-center">Match %</div>
                      <div className="col-span-2 text-center">Action</div>
                    </div>

                    {matchSuggestions.map((suggestion, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 text-sm py-2 border-b hover:bg-white">
                        <div className="col-span-4">
                          <p className="font-medium">{suggestion.product_name}</p>
                          <p className="text-xs text-gray-500">PLU: {suggestion.plu_code}</p>
                        </div>
                        <div className="col-span-2 text-right">R{(suggestion.your_price || 0).toFixed(2)}</div>
                        <div className="col-span-2 text-right">
                          <Badge variant={suggestion.price_difference > 0 ? "destructive" : "default"}>
                            {suggestion.price_difference > 0 ? "+" : ""}R{suggestion.price_difference.toFixed(2)}
                          </Badge>
                        </div>
                        <div className="col-span-2 text-center">
                          <Badge variant={suggestion.confidence === "high" ? "default" : "secondary"}>
                            {suggestion.similarity_score}%
                          </Badge>
                        </div>
                        <div className="col-span-2 text-center">
                          <Button 
                            size="sm"
                            onClick={() => matchVkbProduct(selectedVkbProduct.name, suggestion.product_id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Match
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-gray-500">
                    No automatic matches found. You can manually add this product to competitor tracking.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* PDF Scanner Tab */}
        <TabsContent value="pdf-scanner" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scan className="h-5 w-5 text-blue-600" />
                Competitor PDF Scanner
              </CardTitle>
              <CardDescription>
                Upload any competitor's specials PDF — AI extracts products & prices, then match to your inventory
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div>
                  <Label className="text-sm">Competitor Name</Label>
                  <Input
                    value={pdfCompetitorName}
                    onChange={(e) => setPdfCompetitorName(e.target.value)}
                    placeholder="e.g. NTK, Buildit, Cashbuild"
                    data-testid="pdf-competitor-name"
                  />
                </div>
                <div>
                  <Label className="text-sm">Specials PDF</Label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                    data-testid="pdf-file-input"
                  />
                </div>
                <Button
                  onClick={handlePdfUpload}
                  disabled={pdfScanning || !pdfFile}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="pdf-scan-btn"
                >
                  {pdfScanning ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning PDF...</>
                  ) : (
                    <><Scan className="h-4 w-4 mr-2" /> Scan PDF</>
                  )}
                </Button>
              </div>

              {pdfScanning && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-blue-600" />
                  <p className="text-sm text-blue-700">AI is reading the PDF and extracting products...</p>
                  <p className="text-xs text-blue-500">This may take 30-60 seconds</p>
                </div>
              )}

              {/* Scan Results Summary */}
              {pdfScanResult && (
                <div className="grid grid-cols-3 gap-3">
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-blue-600">{pdfScanResult.competitor} Products Found</p>
                      <p className="text-2xl font-bold text-blue-800">{pdfScanResult.products_found}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-green-600">Pages Read</p>
                      <p className="text-2xl font-bold text-green-800">{pdfScanResult.pages_read}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-amber-600">Needs Matching</p>
                      <p className="text-2xl font-bold text-amber-800">{pdfUnmatched.length}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Match Suggestions Dialog */}
              {selectedPdfProduct && (
                <Card className="border-blue-300 bg-blue-50/50">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{selectedPdfProduct.name}</p>
                        <p className="text-sm text-blue-600">
                          {pdfCompetitorName} Price: <strong>R{selectedPdfProduct.price?.toFixed(2)}</strong>
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setSelectedPdfProduct(null); setPdfMatchSuggestions([]); }}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                    {pdfMatchSuggestions.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500">Select the matching product from your inventory:</p>
                        {pdfMatchSuggestions.map(s => (
                          <div key={s.id} className="flex items-center justify-between bg-white p-2 rounded border">
                            <div>
                              <p className="text-sm font-medium">{s.name}</p>
                              <p className="text-xs text-gray-500">{s.plu_code} | Your price: R{s.selling_price_incl_vat?.toFixed(2)}</p>
                            </div>
                            <Button size="sm" onClick={() => matchPdfProduct(selectedPdfProduct.name, s.id)} data-testid={`match-pdf-${s.plu_code}`}>
                              <Link className="h-3 w-3 mr-1" /> Match
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No similar products found in your inventory</p>
                    )}

                    {/* Manual search */}
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-gray-500 mb-1">Can't find it? Search manually:</p>
                      <div className="relative">
                        <Input
                          value={manualSearch}
                          onChange={(e) => handleManualSearch(e.target.value)}
                          placeholder="Type product name or PLU..."
                          className="w-full"
                          data-testid="manual-match-search"
                        />
                        {manualSearching && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-2.5 text-gray-400" />}
                      </div>
                      {manualResults.length > 0 && (
                        <div className="mt-2 space-y-1 max-h-48 overflow-auto">
                          {manualResults.map(s => (
                            <div key={s.id} className="flex items-center justify-between bg-white p-2 rounded border">
                              <div>
                                <p className="text-sm font-medium">{s.name}</p>
                                <p className="text-xs text-gray-500">{s.plu_code} | R{s.selling_price_incl_vat?.toFixed(2)}</p>
                              </div>
                              <Button size="sm" onClick={() => { matchPdfProduct(selectedPdfProduct.name, s.id); setManualSearch(""); setManualResults([]); }}>
                                <Link className="h-3 w-3 mr-1" /> Match
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Unmatched Products List */}
              {pdfUnmatched.length > 0 && !selectedPdfProduct && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm">{pdfCompetitorName} Products Needing Match ({pdfUnmatched.length})</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2 font-medium">Product Name</th>
                          <th className="text-left p-2 font-medium">Category</th>
                          <th className="text-right p-2 font-medium">{pdfCompetitorName} Price</th>
                          <th className="text-center p-2 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pdfUnmatched.map((p, i) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="p-2">{p.name}</td>
                            <td className="p-2 text-gray-500">{p.category}</td>
                            <td className="p-2 text-right font-mono font-semibold">R{p.price?.toFixed(2)}</td>
                            <td className="p-2 text-center">
                              <Button size="sm" variant="outline" onClick={() => fetchPdfSuggestions(p.name)} data-testid={`find-match-pdf-${i}`}>
                                <Link className="h-3 w-3 mr-1" /> Find Match
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Extracted Products Preview */}
              {pdfProducts.length > 0 && !pdfUnmatched.length && !selectedPdfProduct && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm">Extracted Products ({pdfProducts.length})</h3>
                  <div className="border rounded-lg overflow-auto max-h-[400px]">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">Product</th>
                          <th className="text-left p-2 font-medium">Category</th>
                          <th className="text-right p-2 font-medium">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pdfProducts.map((p, i) => (
                          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="p-2">{p.name}</td>
                            <td className="p-2 text-gray-500">{p.category}</td>
                            <td className="p-2 text-right font-mono">R{Number(p.price).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Price Health Check Tab */}
        <TabsContent value="price-check" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Price Health Check
                  </CardTitle>
                  <CardDescription>
                    Scan all products for pricing errors: cost mismatches, zero markup, selling below cost, and more
                  </CardDescription>
                </div>
                <Button
                  onClick={runPriceCheck}
                  disabled={priceCheckLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="run-price-check-btn"
                >
                  {priceCheckLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning...</>
                  ) : (
                    <><Scan className="h-4 w-4 mr-2" /> Run Price Check</>
                  )}
                </Button>
              </div>
            </CardHeader>
            {priceCheckData && (
              <CardContent className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-5 gap-3">
                  <Card className="border-gray-200">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-gray-500">Products Checked</p>
                      <p className="text-xl font-bold">{priceCheckData.summary.total_products_checked}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-amber-600">With Issues</p>
                      <p className="text-xl font-bold text-amber-700">{priceCheckData.summary.products_with_issues}</p>
                    </CardContent>
                  </Card>
                  <Card className={`cursor-pointer ${priceCheckFilter === 'HIGH' ? 'ring-2 ring-red-400' : ''} border-red-200 bg-red-50`} onClick={() => setPriceCheckFilter(priceCheckFilter === 'HIGH' ? 'ALL' : 'HIGH')}>
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-red-600">High Severity</p>
                      <p className="text-xl font-bold text-red-700">{priceCheckData.summary.high_severity}</p>
                    </CardContent>
                  </Card>
                  <Card className={`cursor-pointer ${priceCheckFilter === 'MEDIUM' ? 'ring-2 ring-orange-400' : ''} border-orange-200 bg-orange-50`} onClick={() => setPriceCheckFilter(priceCheckFilter === 'MEDIUM' ? 'ALL' : 'MEDIUM')}>
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-orange-600">Medium</p>
                      <p className="text-xl font-bold text-orange-700">{priceCheckData.summary.medium_severity}</p>
                    </CardContent>
                  </Card>
                  <Card className={`cursor-pointer ${priceCheckFilter === 'LOW' ? 'ring-2 ring-yellow-400' : ''} border-yellow-200 bg-yellow-50`} onClick={() => setPriceCheckFilter(priceCheckFilter === 'LOW' ? 'ALL' : 'LOW')}>
                    <CardContent className="p-3 text-center">
                      <p className="text-xs text-yellow-600">Low</p>
                      <p className="text-xl font-bold text-yellow-700">{priceCheckData.summary.low_severity}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Issues Table */}
                <div className="border rounded-lg overflow-auto max-h-[500px]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium">Product</th>
                        <th className="text-right p-2 font-medium">Selling</th>
                        <th className="text-right p-2 font-medium">Last Cost</th>
                        <th className="text-right p-2 font-medium">Avg Cost</th>
                        <th className="text-right p-2 font-medium">Markup</th>
                        <th className="text-right p-2 font-medium">Stock</th>
                        <th className="text-left p-2 font-medium">Issues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceCheckData.issues
                        .filter(item => priceCheckFilter === 'ALL' || item.max_severity === priceCheckFilter)
                        .map((item, idx) => (
                        <tr key={item.id} className={`border-t ${item.max_severity === 'HIGH' ? 'bg-red-50/50' : item.max_severity === 'MEDIUM' ? 'bg-orange-50/50' : ''}`}>
                          <td className="p-2">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-xs text-gray-400">{item.plu_code} {item.supplier ? `| ${item.supplier}` : ''}</div>
                          </td>
                          <td className="p-2 text-right font-mono">R{item.selling_price.toFixed(2)}</td>
                          <td className="p-2 text-right font-mono">R{item.last_cost.toFixed(2)}</td>
                          <td className="p-2 text-right font-mono">R{item.avg_cost.toFixed(2)}</td>
                          <td className="p-2 text-right font-mono">{item.markup.toFixed(1)}%</td>
                          <td className="p-2 text-right">{item.stock}</td>
                          <td className="p-2">
                            {item.issues.map((issue, i) => (
                              <div key={i} className="mb-1">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium mr-1 ${
                                  issue.severity === 'HIGH' ? 'bg-red-100 text-red-700' :
                                  issue.severity === 'MEDIUM' ? 'bg-orange-100 text-orange-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {issue.type.replace(/_/g, ' ')}
                                </span>
                                <span className="text-xs text-gray-600">{issue.message}</span>
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* Slow Movers Tab */}
        <TabsContent value="slow-movers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-orange-500" />
                Slow-Moving Inventory
              </CardTitle>
              <CardDescription>
                Products with high stock levels and low sales velocity - consider discounts to move inventory
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={fetchSlowMovers} disabled={loading} className="mb-4">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Analyze Slow Movers
              </Button>

              {slowMovers.length > 0 && (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 font-semibold text-sm border-b pb-2">
                    <div className="col-span-3">Product</div>
                    <div className="col-span-1 text-right">Stock</div>
                    <div className="col-span-1 text-right">Sold</div>
                    <div className="col-span-2 text-center">Days of Stock</div>
                    <div className="col-span-2 text-right">Selling Price</div>
                    <div className="col-span-2 text-right">Suggested Price</div>
                    <div className="col-span-1 text-center">Priority</div>
                  </div>

                  {slowMovers.map((product, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 text-sm py-2 border-b hover:bg-gray-50">
                      <div className="col-span-3">
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-gray-500">PLU: {product.plu_code} | {product.category}</p>
                      </div>
                      <div className="col-span-1 text-right">{product.current_stock}</div>
                      <div className="col-span-1 text-right text-gray-600">{product.sales_last_period}</div>
                      <div className="col-span-2 text-center">
                        <Badge variant={product.days_of_stock > 180 ? "destructive" : "secondary"}>
                          {product.days_of_stock}+ days
                        </Badge>
                      </div>
                      <div className="col-span-2 text-right">
                        R{product.current_price?.toFixed(2)}
                        <p className="text-xs text-gray-500">{product.current_markup}% markup</p>
                      </div>
                      <div className="col-span-2 text-right text-green-600 font-semibold">
                        R{product.recommended_price?.toFixed(2)}
                        <p className="text-xs">(-{product.recommended_discount}%)</p>
                      </div>
                      <div className="col-span-1 text-center">
                        <Badge variant={product.priority === "high" ? "destructive" : "secondary"}>
                          {product.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {slowMovers.length === 0 && !loading && (
                <p className="text-center text-gray-500 py-8">
                  Click "Analyze Slow Movers" to find products that need price adjustments
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* High Margin Opportunities Tab */}
        <TabsContent value="margin-opportunities" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                High Margin Opportunities
              </CardTitle>
              <CardDescription>
                Fast-moving products where you can potentially increase prices for better margins
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={fetchHighMargin} disabled={loading} className="mb-4">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Find Opportunities
              </Button>

              {highMargin.length > 0 && (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 font-semibold text-sm border-b pb-2">
                    <div className="col-span-3">Product</div>
                    <div className="col-span-2 text-right">Sales (30d)</div>
                    <div className="col-span-2 text-right">Current Price</div>
                    <div className="col-span-2 text-right">Suggested Price</div>
                    <div className="col-span-2 text-right">Revenue Impact</div>
                    <div className="col-span-1 text-center">Confidence</div>
                  </div>

                  {highMargin.map((product, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 text-sm py-2 border-b hover:bg-gray-50">
                      <div className="col-span-3">
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-gray-500">PLU: {product.plu_code}</p>
                      </div>
                      <div className="col-span-2 text-right">{product.sales_last_30_days} units</div>
                      <div className="col-span-2 text-right">
                        R{product.current_price?.toFixed(2)}
                        <p className="text-xs text-gray-500">{product.current_markup}% markup</p>
                      </div>
                      <div className="col-span-2 text-right text-green-600 font-semibold">
                        R{product.recommended_price?.toFixed(2)}
                        <p className="text-xs">(+{product.suggested_increase_pct}%)</p>
                      </div>
                      <div className="col-span-2 text-right font-semibold text-green-600">
                        +R{product.monthly_revenue_impact?.toFixed(2)}
                      </div>
                      <div className="col-span-1 text-center">
                        <Badge variant={product.confidence === "high" ? "default" : "secondary"}>
                          {product.confidence}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {highMargin.length === 0 && !loading && (
                <p className="text-center text-gray-500 py-8">
                  Click "Find Opportunities" to discover margin improvement possibilities
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* New Products Pricing Tab */}
        <TabsContent value="new-products" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-500" />
                New Product Pricing Guide
              </CardTitle>
              <CardDescription>
                Recommended markups by category based on your existing products
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={fetchNewProductPricing} disabled={loading} className="mb-4">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Load Pricing Guide
              </Button>

              {Object.keys(newProductPricing).length > 0 && (
                <div className="space-y-2">
                  <div className="grid grid-cols-6 gap-2 font-semibold text-sm border-b pb-2">
                    <div className="col-span-2">Category</div>
                    <div className="col-span-1 text-right">Avg Markup</div>
                    <div className="col-span-1 text-right">Median</div>
                    <div className="col-span-1 text-right">Range</div>
                    <div className="col-span-1 text-right">Products</div>
                  </div>

                  {Object.entries(newProductPricing).map(([category, stats]) => (
                    <div key={category} className="grid grid-cols-6 gap-2 text-sm py-2 border-b hover:bg-gray-50">
                      <div className="col-span-2 font-medium">{category}</div>
                      <div className="col-span-1 text-right">{stats.average_markup}%</div>
                      <div className="col-span-1 text-right">
                        <Badge variant="default">{stats.recommended_markup}%</Badge>
                      </div>
                      <div className="col-span-1 text-right text-xs">
                        {stats.min_markup}-{stats.max_markup}%
                      </div>
                      <div className="col-span-1 text-right text-gray-500">{stats.product_count}</div>
                    </div>
                  ))}
                </div>
              )}

              {Object.keys(newProductPricing).length === 0 && !loading && (
                <p className="text-center text-gray-500 py-8">
                  Click "Load Pricing Guide" to see recommended markups by category
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Competitor Prices Tab */}
        <TabsContent value="competitor-prices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-green-500" />
                Competitor Price Tracking
              </CardTitle>
              <CardDescription>
                Track VKB LANDBOU (NTK LIMPOPO) and other competitor prices
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Button onClick={() => setShowAddCompetitor(!showAddCompetitor)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Competitor Price
                </Button>
                <Button onClick={fetchPriceComparison} variant="outline" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Compare Prices
                </Button>
                <Button variant="outline" onClick={() => window.open("https://www.vkb.co.za", "_blank")}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Visit VKB Website
                </Button>
              </div>

              {/* Add Competitor Price Form */}
              {showAddCompetitor && (
                <Card className="mb-4 bg-gray-50">
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Product ID (from your inventory)</Label>
                        <Input
                          value={competitorForm.product_id}
                          onChange={(e) => setCompetitorForm({...competitorForm, product_id: e.target.value})}
                          placeholder="Enter product ID or PLU"
                        />
                      </div>
                      <div>
                        <Label>Competitor</Label>
                        <Input
                          value={competitorForm.competitor_name}
                          onChange={(e) => setCompetitorForm({...competitorForm, competitor_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Their Price (R)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={competitorForm.competitor_price}
                          onChange={(e) => setCompetitorForm({...competitorForm, competitor_price: e.target.value})}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label>Source URL (optional)</Label>
                        <Input
                          value={competitorForm.source_url}
                          onChange={(e) => setCompetitorForm({...competitorForm, source_url: e.target.value})}
                          placeholder="https://..."
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Notes (optional)</Label>
                        <Input
                          value={competitorForm.notes}
                          onChange={(e) => setCompetitorForm({...competitorForm, notes: e.target.value})}
                          placeholder="Any additional notes..."
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button onClick={addCompetitorPrice}>Add Price</Button>
                      <Button variant="outline" onClick={() => setShowAddCompetitor(false)}>Cancel</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Price Comparison Results */}
              {priceComparison.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold mb-2">Price Comparison Results</h3>
                  <div className="grid grid-cols-12 gap-2 font-semibold text-sm border-b pb-2">
                    <div className="col-span-3">Product</div>
                    <div className="col-span-2 text-right">Your Price</div>
                    <div className="col-span-2 text-right">VKB Price</div>
                    <div className="col-span-2 text-right">Difference</div>
                    <div className="col-span-3">Recommendation</div>
                  </div>

                  {priceComparison.map((comp, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 text-sm py-2 border-b hover:bg-gray-50">
                      <div className="col-span-3">
                        <p className="font-medium">{comp.name}</p>
                        <p className="text-xs text-gray-500">PLU: {comp.plu_code}</p>
                      </div>
                      <div className="col-span-2 text-right">R{comp.your_price}</div>
                      <div className="col-span-2 text-right">R{comp.competitor_price}</div>
                      <div className="col-span-2 text-right">
                        <Badge variant={comp.difference > 0 ? "destructive" : "default"}>
                          {comp.difference > 0 ? "+" : ""}R{comp.difference} ({comp.difference_pct}%)
                        </Badge>
                      </div>
                      <div className="col-span-3 text-xs">
                        {comp.recommendation}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Category Performance Tab */}
        <TabsContent value="category-performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-purple-500" />
                Category Performance
              </CardTitle>
              <CardDescription>
                Analyze which product categories are generating the most revenue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={fetchCategoryPerformance} disabled={loading} className="mb-4">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Analyze Categories
              </Button>

              {categoryPerformance.length > 0 && (
                <div className="space-y-2">
                  <div className="grid grid-cols-5 gap-2 font-semibold text-sm border-b pb-2">
                    <div className="col-span-2">Category</div>
                    <div className="col-span-1 text-right">Units Sold</div>
                    <div className="col-span-1 text-right">Revenue</div>
                    <div className="col-span-1 text-right">Avg Transaction</div>
                  </div>

                  {categoryPerformance.map((cat, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 text-sm py-2 border-b hover:bg-gray-50">
                      <div className="col-span-2 font-medium">{cat.category}</div>
                      <div className="col-span-1 text-right">{cat.units_sold}</div>
                      <div className="col-span-1 text-right font-semibold text-green-600">
                        R{cat.revenue.toLocaleString()}
                      </div>
                      <div className="col-span-1 text-right">R{cat.avg_transaction_value}</div>
                    </div>
                  ))}
                </div>
              )}

              {categoryPerformance.length === 0 && !loading && (
                <p className="text-center text-gray-500 py-8">
                  Click "Analyze Categories" to see category performance data
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIFeatures;
